import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '../auth/useAuth';
import { shellApi } from './api';
import { useLeaveRequestCount } from '../leave/hooks';
import type { NavBadgeKey } from '../../components/layout/nav';

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useShellSearch(term: string, opts: { employees: boolean; devices: boolean }) {
  const q = useDebounced(term.trim(), 250);
  const enabled = q.length >= 2;
  const employees = useQuery({
    queryKey: ['shell', 'search', 'employees', q],
    queryFn: () => shellApi.searchEmployees(q),
    enabled: enabled && opts.employees,
    staleTime: 30_000,
  });
  const devices = useQuery({
    queryKey: ['shell', 'search', 'devices', q],
    queryFn: () => shellApi.searchDevices(q),
    enabled: enabled && opts.devices,
    staleTime: 30_000,
  });
  return { q, enabled, employees, devices };
}

const INBOX = ['shell', 'inbox'] as const;
const UNREAD = ['shell', 'inbox', 'unread'] as const;

/** Polled lightly: the bell only needs a count, and the list loads on open. */
export function useUnreadCount() {
  return useQuery({
    queryKey: UNREAD,
    queryFn: shellApi.unreadCount,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

export function useInbox(open: boolean) {
  return useQuery({ queryKey: INBOX, queryFn: shellApi.inbox, enabled: open });
}

/** One page of the full Notifications page. */
export function useInboxPage(page: number, perPage: number) {
  return useQuery({
    queryKey: [...INBOX, 'page', page, perPage] as const,
    queryFn: () => shellApi.inboxPage(page, perPage),
    placeholderData: keepPreviousData,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => shellApi.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shell', 'inbox'] }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => shellApi.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shell', 'inbox'] }),
  });
}

export function useFleetSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['devices', 'fleet-summary'],
    queryFn: shellApi.fleetSummary,
    enabled,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
}

/** Whether the signed-in role can use any search at all. */
export function useSearchAccess() {
  const permissions = usePermissions();
  const canEmployees = permissions.can('employee.view');
  const canDevices = permissions.can('device.view');
  return { canEmployees, canDevices, any: canEmployees || canDevices };
}

/** Live counts for the sidebar badges, each gated by the same permission as its nav item. */
export function useNavBadges(): Partial<Record<NavBadgeKey, number>> {
  const permissions = usePermissions();
  const canLeave = permissions.canAny(['leave.view', 'leave.approve']);
  const leave = useLeaveRequestCount({ status: 'pending' }, canLeave);
  const unread = useUnreadCount();
  const out: Partial<Record<NavBadgeKey, number>> = {};
  if (canLeave && leave.data !== undefined) out.pendingLeave = leave.data;
  if (unread.data) out.unreadNotifications = unread.data.unread;
  return out;
}

/**
 * `GET /tenant/setup-status` (Re-design T08). Fresh for 60s and refetched on window
 * focus, so the dashboard and sidebar leave setup mode on their own once the tenant
 * is up and running. `enabled` is the caller's `tenant.edit` — only admins see setup UI.
 */
export function useSetupStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['setup-status'],
    queryFn: shellApi.setupStatus,
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}
